/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

const FALLBACK_ASCENT_RATIO = 0.8;
const FALLBACK_DESCENT_RATIO = 0.2;

/**
 * Returns the vertical offset required to put a differently sized list marker
 * on the first text line's baseline. Both runs use the same font and unitless
 * line height, so their baseline positions differ only by their font sizes.
 *
 * @param {Object} options Calculation options.
 * @param {Object} options.fontMetrics Font metrics for the text element.
 * @param {number} options.fontSize Text font size.
 * @param {number} options.lineHeight Unitless line height.
 * @param {number} options.markerSize Marker font size.
 * @param {number} options.markerBaselineShift Manual baseline shift. Positive
 * values move the marker upward, matching professional typography controls.
 * @return {number} Vertical offset in the same units as the font sizes.
 */
function getListMarkerBaselineOffset({
  fontMetrics,
  fontSize,
  lineHeight,
  markerSize,
  markerBaselineShift = 0,
}) {
  const hasFontMetrics =
    fontMetrics?.upm > 0 &&
    Number.isFinite(fontMetrics.asc) &&
    Number.isFinite(fontMetrics.des);
  const ascentRatio = hasFontMetrics
    ? fontMetrics.asc / fontMetrics.upm
    : FALLBACK_ASCENT_RATIO;
  const descentRatio = hasFontMetrics
    ? Math.abs(fontMetrics.des) / fontMetrics.upm
    : FALLBACK_DESCENT_RATIO;
  const numericLineHeight = Number(lineHeight);
  const resolvedLineHeight =
    Number.isFinite(numericLineHeight) && numericLineHeight > 0
      ? numericLineHeight
      : 1;
  const baselineRatio = (resolvedLineHeight + ascentRatio - descentRatio) / 2;

  const automaticOffset = (fontSize - markerSize) * baselineRatio;

  return Math.round((automaticOffset - markerBaselineShift) * 10000) / 10000;
}

export default getListMarkerBaselineOffset;
